import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import MicNoneIcon from '@mui/icons-material/MicNone';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useRef, useState } from 'react';

import AttachButtonHint from '~components/ComposerAttachments/AttachButtonHint';
import AttachmentDropOverlay from '~components/ComposerAttachments/AttachmentDropOverlay';
import ComposerAttachments from '~components/ComposerAttachments/ComposerAttachments';
import {
  SmallButton,
  squareIconButtonSx,
} from '~components/Order/ordersDispatchStyledComponents';
import { TREAD_AGENT_ATTACHMENT_CONFIG } from '~hooks/useComposerAttachments/composerAttachmentConfigs';
import { ComposerAttachment } from '~hooks/useComposerAttachments/useComposerAttachments';
import useFileDropTarget from '~hooks/useComposerAttachments/useFileDropTarget';
import { TreadAgentSkillBasic } from '~hooks/useTreadAgent/models';
import DictationWaveform from '~tread-agent/DictationWaveform';
import SkillPickerMenu, {
  SKILL_PICKER_LISTBOX_ID,
  skillPickerOptionId,
} from '~tread-agent/SkillPickerMenu';
import useDictation from '~tread-agent/useDictation';
import useSkillPicker from '~tread-agent/useSkillPicker';

// brandV2Black is a fixed navy in both themes and vanishes against the dark-mode surface, so
// an outlined variant has to drive its border and icon from mode-aware tokens.
const stopButtonSx = {
  ...squareIconButtonSx,
  borderColor: 'divider',
  color: 'text.primary',
};

interface ChatInputProps {
  attachmentsBlockSend: boolean;
  attachments: ComposerAttachment[];
  errorMessage?: string | null;
  isAtAttachmentLimit: boolean;
  isDisabled: boolean;
  isStopRequested: boolean;
  isTurnInProgress: boolean;
  minRows?: number;
  onAddFiles: (files: File[]) => void;
  onRemoveAttachment: (attachmentId: string) => void;
  onSend: () => void;
  onStop: () => void;
  onValueChange: (value: string) => void;
  placeholder?: string;
  skills: TreadAgentSkillBasic[];
  value: string;
}

export default function ChatInput({
  attachmentsBlockSend,
  attachments,
  errorMessage,
  isAtAttachmentLimit,
  isDisabled,
  isStopRequested,
  isTurnInProgress,
  minRows,
  onAddFiles,
  onRemoveAttachment,
  onSend,
  onStop,
  onValueChange,
  placeholder,
  skills,
  value,
}: ChatInputProps) {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composerInputRef = useRef<HTMLTextAreaElement>(null);
  const [composerElement, setComposerElement] = useState<HTMLElement | null>(null);
  const { dropTargetProps, isDraggingOver } = useFileDropTarget(
    isDisabled ? null : onAddFiles,
  );
  const [dictationStartFailed, setDictationStartFailed] = useState(false);

  const hasUploadedAttachment = attachments.some(
    (attachment) => attachment.status === 'uploaded',
  );
  // A message needs either text or at least one finished upload, and can't send while an
  // upload is still in flight (we need its signed id) or while an attachment error stands —
  // a refused selection must not go out as a message the sender believes carries their files.
  const canSend =
    !isDisabled &&
    !attachmentsBlockSend &&
    (value.trim().length > 0 || hasUploadedAttachment);

  const {
    isDictating,
    isDictationSupported,
    isMicrophoneBlocked,
    startDictation,
    stopDictation,
  } = useDictation({ isDisabled, onValueChange, value });

  const {
    activeIndex: skillPickerActiveIndex,
    browseSkills,
    dismissPicker,
    handleKeyDown: handleSkillPickerKeyDown,
    isOpen: isSkillPickerOpen,
    matchingSkills,
    selectSkill,
    setActiveIndex: setSkillPickerActiveIndex,
  } = useSkillPicker({ onValueChange, skills, value });

  const dictationErrorMessage = isMicrophoneBlocked
    ? t('tread_agent.dictation.microphone_blocked')
    : dictationStartFailed
      ? t('tread_agent.dictation.start_failed')
      : null;
  const displayedErrorMessage = errorMessage ?? dictationErrorMessage;

  const handleSend = useCallback(() => {
    void stopDictation();
    onSend();
  }, [onSend, stopDictation]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (handleSkillPickerKeyDown(event)) {
        return;
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();

        if (canSend) {
          handleSend();
        }
      }
    },
    [canSend, handleSend, handleSkillPickerKeyDown],
  );

  // Scoped to the whole composer rather than the menu, so clicking back into the textarea
  // or onto the toolbar does not count as clicking away.
  const handleClickAway = useCallback(() => {
    if (isSkillPickerOpen) {
      dismissPicker();
    }
  }, [dismissPicker, isSkillPickerOpen]);

  const handleShowSkillsClick = useCallback(() => {
    browseSkills();
    composerInputRef.current?.focus();
  }, [browseSkills]);

  const handleDictationClick = useCallback(async () => {
    if (isDictating) {
      await stopDictation();

      return;
    }

    setDictationStartFailed(false);

    try {
      await startDictation();
    } catch (error) {
      console.error('Failed to start dictation:', error);
      setDictationStartFailed(true);
    }
  }, [isDictating, startDictation, stopDictation]);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(event.target.files ?? []);

      if (selectedFiles.length > 0) {
        onAddFiles(selectedFiles);
      }

      // Reset so selecting the same file again still fires a change event.
      event.target.value = '';
    },
    [onAddFiles],
  );

  // Column layout: the growing text sits on top and the send button pins to a bottom row,
  // so the button rides down with the text as it wraps to multiple lines. The composer is an
  // enclosed, inset rounded box docked at the bottom of the panel.
  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        data-test-id="tread-agent-composer"
        {...dropTargetProps}
        ref={setComposerElement}
        sx={{
          border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          borderRadius: theme.brandV2.borderRadius,
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          m: 1.5,
          p: 1,
          position: 'relative',
          ...(isDraggingOver ? { borderColor: theme.brandV2.colors.treadBlue } : {}),
        }}
      >
        {isDraggingOver && (
          <AttachmentDropOverlay label={t('tread_agent.attachments.drop_to_attach')} />
        )}

        <SkillPickerMenu
          activeIndex={skillPickerActiveIndex}
          anchorEl={composerElement}
          isOpen={isSkillPickerOpen}
          onHighlight={setSkillPickerActiveIndex}
          onSelect={selectSkill}
          skills={matchingSkills}
        />

        <ComposerAttachments attachments={attachments} onRemove={onRemoveAttachment} />

        <InputBase
          disabled={isDisabled}
          fullWidth
          maxRows={8}
          minRows={minRows}
          multiline
          onChange={(event) => onValueChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? t('tread_agent.message_placeholder')}
          slotProps={{
            input: {
              'aria-activedescendant': isSkillPickerOpen
                ? skillPickerOptionId(skillPickerActiveIndex)
                : undefined,
              'aria-controls': isSkillPickerOpen ? SKILL_PICKER_LISTBOX_ID : undefined,
              'aria-expanded': isSkillPickerOpen,
              role: isSkillPickerOpen ? 'combobox' : undefined,
            },
          }}
          inputRef={composerInputRef}
          sx={{ fontSize: '13px', px: 0.5 }}
          value={value}
        />

        {displayedErrorMessage && (
          <Typography
            sx={{ color: theme.brandV2.colors.treadRed, fontSize: '12px', px: 0.5 }}
          >
            {displayedErrorMessage}
          </Typography>
        )}

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.25 }}>
            <IconButton
              aria-label={t('tread_agent.show_skills')}
              data-test-id="tread-agent-show-skills"
              disabled={isDisabled || skills.length === 0}
              onClick={handleShowSkillsClick}
              size="small"
            >
              <LayersOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <input
              accept={TREAD_AGENT_ATTACHMENT_CONFIG.accept}
              data-test-id="tread-agent-file-input"
              hidden
              multiple
              onChange={handleFileInputChange}
              ref={fileInputRef}
              type="file"
            />

            <AttachButtonHint
              attachmentCount={attachments.length}
              config={TREAD_AGENT_ATTACHMENT_CONFIG}
              isDisabled={isDisabled}
            >
              {(hintId) => (
                <IconButton
                  aria-describedby={hintId}
                  aria-label={t('attachments.attach_file')}
                  disabled={isDisabled || isAtAttachmentLimit}
                  onClick={handleAttachClick}
                  size="small"
                >
                  <AttachFileIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </AttachButtonHint>
          </Box>

          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            {isDictationSupported && (
              <IconButton
                aria-label={
                  isDictating
                    ? t('tread_agent.dictation.stop')
                    : t('tread_agent.dictation.start')
                }
                aria-pressed={isDictating}
                data-test-id="tread-agent-dictation"
                disabled={isDisabled}
                onClick={() => void handleDictationClick()}
                size="small"
              >
                {isDictating ? (
                  <Box sx={{ color: theme.brandV2.colors.treadBlue, display: 'flex' }}>
                    <DictationWaveform />
                  </Box>
                ) : (
                  <MicNoneIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            )}

            {isTurnInProgress ? (
              <SmallButton
                aria-label={t('actions.stop')}
                color="brandV2Black"
                disabled={isStopRequested}
                onClick={onStop}
                sx={stopButtonSx}
                variant="outlined"
              >
                <StopCircleOutlinedIcon sx={{ fontSize: 18 }} />
              </SmallButton>
            ) : (
              <SmallButton
                aria-label={t('actions.send')}
                color="brandV2Black"
                disabled={!canSend}
                onClick={handleSend}
                sx={squareIconButtonSx}
              >
                <ArrowUpwardIcon sx={{ fontSize: 18 }} />
              </SmallButton>
            )}
          </Box>
        </Box>
      </Box>
    </ClickAwayListener>
  );
}
